/**
 * useCustomers Hook
 *
 * Issue #2471: Orval自動生成hookへの全面移行
 *
 * Orval生成hookの薄いラッパー。呼び出し側の変更を最小化するため
 * 既存のシグネチャを維持しつつ、内部実装をOrval生成関数に委譲する。
 *
 * **Features**:
 * - Automatic loading state via isLoading/isFetching
 * - Error handling with typed errors
 * - Cache management with 60s stale time
 * - Background refetch on window focus
 * - Realtime update integration ready
 *
 * @example
 * ```typescript
 * const { data: customers, isLoading, error, refetch } = useCustomers();
 *
 * if (isLoading) return <Skeleton />;
 * if (error) return <ErrorAlert message={error.message} />;
 *
 * return <CustomerList customers={customers} />;
 * ```
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import {
  customersControllerFindAll,
  customersControllerCreate,
  customersControllerUpdate,
  customersControllerRemove,
  getCustomersControllerFindAllQueryKey,
} from '@/generated/api/顧客管理/顧客管理';
import type { Customer } from '@/utils/localStorage';
import type { CreateCustomerDto } from '@/services/customersService';
import logger from '@/utils/logger';

/** Query key for customers - uses Orval-generated key for cache consistency */
export const CUSTOMERS_QUERY_KEY = getCustomersControllerFindAllQueryKey();

/** 顧客一括取得の上限値（全件取得用）*/
export const CUSTOMER_FETCH_LIMIT = 100;
const CUSTOMER_FETCH_MAX_PAGES = 200;

type CustomerPage = {
  customers: Customer[];
  total?: number;
  limit?: number;
  page?: number;
};

/**
 * APIレスポンスから Customer[] を抽出する共有ヘルパー
 *
 * NestJS TransformInterceptor は { data: T[], total: number } にラップするが、
 * custom-instance の interceptor がアンラップ済みの場合は T[] が直接返る。
 * 両パターンに対応し、常に Customer[] を返す。
 */
export function extractCustomerArray(result: unknown): Customer[] {
  if (Array.isArray(result)) return result;
  if (
    result !== null &&
    typeof result === 'object' &&
    'data' in result &&
    Array.isArray((result as { data: unknown }).data)
  ) {
    return (result as { data: Customer[] }).data;
  }
  logger.warn('[extractCustomerArray] Unexpected API response format', {
    type: typeof result,
    value: result,
  });
  return [];
}

function extractCustomerPage(result: unknown): CustomerPage {
  if (Array.isArray(result)) {
    return {
      customers: result,
      total: result.length,
      limit: result.length,
      page: 1,
    };
  }

  if (
    result !== null &&
    typeof result === 'object' &&
    'data' in result &&
    Array.isArray((result as { data: unknown }).data)
  ) {
    const pageResult = result as {
      data: Customer[];
      total?: unknown;
      limit?: unknown;
      page?: unknown;
    };
    return {
      customers: pageResult.data,
      total:
        typeof pageResult.total === 'number' ? pageResult.total : undefined,
      limit:
        typeof pageResult.limit === 'number' ? pageResult.limit : undefined,
      page: typeof pageResult.page === 'number' ? pageResult.page : undefined,
    };
  }

  return {
    customers: extractCustomerArray(result),
  };
}

export async function fetchAllCustomers(): Promise<Customer[]> {
  const customers: Customer[] = [];
  let page = 1;

  while (page > 0 && page <= CUSTOMER_FETCH_MAX_PAGES) {
    const result = await customersControllerFindAll({
      limit: CUSTOMER_FETCH_LIMIT,
      page,
    });
    const customerPage = extractCustomerPage(result);

    customers.push(...customerPage.customers);

    const responsePage = customerPage.page ?? page;
    const responseLimit = customerPage.limit ?? CUSTOMER_FETCH_LIMIT;
    const total = customerPage.total;

    if (
      typeof total !== 'number' ||
      responseLimit <= 0 ||
      responsePage * responseLimit >= total
    ) {
      return customers;
    }

    page = responsePage + 1;
  }

  if (page > CUSTOMER_FETCH_MAX_PAGES) {
    Sentry.captureMessage('customers-fetch-max-pages-exceeded', {
      level: 'warning',
      extra: {
        source: 'customers-fetch',
        endpoint: '/api/v1/customers',
        page,
        limit: CUSTOMER_FETCH_LIMIT,
        fetchedCount: customers.length,
      },
    });
  }

  return customers;
}

/** toCreateCustomerDtoの入力型 — 必須3フィールド + オプション14フィールド */
export interface CustomerDtoSource {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  nameKana?: string;
  email?: string;
  phoneSecondary?: string;
  birthDate?: string;
  gender?: 'male' | 'female' | 'other' | 'unspecified';
  address?: string;
  memo?: string;
  rank?: 'bronze' | 'silver' | 'gold' | 'platinum';
  tags?: string[];
  bookingSource?:
    | 'unspecified'
    | 'hotpepper'
    | 'rakuten'
    | 'google'
    | 'instagram'
    | 'referral'
    | 'walk-in'
    | 'other';
  preferredStaff?: string;
  preferredStaffId?: string;
  medicalInfo?: CreateCustomerDto['medicalInfo'];
  isVip?: boolean;
  firstVisitDate?: string;
}

/**
 * Customer → CreateCustomerDto 変換
 * バックエンドDTOの必須/オプションフィールドのみを明示的にpick
 * Issue #2471: customersService.ts の CreateCustomerDto (Orval生成型ベース) に準拠
 */
export function toCreateCustomerDto(
  customer: CustomerDtoSource
): CreateCustomerDto {
  const {
    firstName,
    lastName,
    phoneNumber,
    nameKana,
    email,
    phoneSecondary,
    birthDate,
    gender,
    address,
    memo,
    rank,
    tags,
    bookingSource,
    preferredStaff,
    preferredStaffId,
    medicalInfo,
    isVip,
    firstVisitDate,
  } = customer;
  return {
    firstName,
    lastName,
    phoneNumber,
    nameKana,
    email,
    phoneSecondary,
    birthDate,
    gender,
    address,
    memo,
    rank,
    tags,
    bookingSource,
    preferredStaff,
    preferredStaffId,
    medicalInfo,
    isVip,
    firstVisitDate,
  };
}

/**
 * useCustomers Hook
 *
 * Fetches all customers with TanStack Query (Orval-generated).
 * Returns paginated data (limit=100 chunks) flattened to array.
 *
 * @returns TanStack Query result with customers data
 */
export function useCustomers() {
  return useQuery<Customer[], Error>({
    queryKey: CUSTOMERS_QUERY_KEY,
    queryFn: fetchAllCustomers,
    staleTime: 60 * 1000, // 60秒間はキャッシュを fresh として扱う
    refetchOnWindowFocus: true,
  });
}

/**
 * useCustomer Hook
 *
 * Fetches a single customer by ID from the cached customers list.
 *
 * @param customerId - The customer ID to find
 * @returns TanStack Query result with single customer
 */
export function useCustomer(customerId: number | string | null) {
  const { data: customers, ...rest } = useCustomers();

  return {
    ...rest,
    data: customers?.find((c) => String(c.id) === String(customerId)) ?? null,
  };
}

/**
 * useAddCustomer Hook
 *
 * Mutation hook for adding a new customer.
 *
 * @returns TanStack Mutation result
 */
export function useAddCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['customers', 'add'],
    mutationFn: async (customerData: CustomerDtoSource) => {
      const result = await customersControllerCreate(
        toCreateCustomerDto(customerData) as Parameters<
          typeof customersControllerCreate
        >[0]
      );
      return result as unknown as Customer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
    },
    onError: (error) => {
      logger.error('[useAddCustomer] Customer creation failed', { error });
    },
  });
}

/**
 * useUpdateCustomer Hook
 *
 * Mutation hook for updating an existing customer.
 *
 * @returns TanStack Mutation result
 */
export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['customers', 'update'],
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Customer>;
    }) => {
      const result = await customersControllerUpdate(
        id,
        updates as Parameters<typeof customersControllerUpdate>[1]
      );
      return result as unknown as Customer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
    },
    onError: (error: Error) => {
      logger.error('[useUpdateCustomer] 顧客更新失敗:', error);
    },
  });
}

/**
 * useDeleteCustomer Hook
 *
 * Mutation hook for deleting a customer.
 *
 * @returns TanStack Mutation result
 */
export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['customers', 'delete'],
    mutationFn: (id: string) => customersControllerRemove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY });
    },
    onError: (error: Error) => {
      logger.error('[useDeleteCustomer] 顧客削除失敗:', error);
    },
  });
}

/**
 * useRefreshCustomers Hook
 *
 * Provides a function to manually refresh customers data.
 * Useful for realtime update handlers.
 *
 * @returns Object with refetch function
 */
export function useRefreshCustomers() {
  const queryClient = useQueryClient();

  return {
    refresh: () =>
      queryClient.invalidateQueries({ queryKey: CUSTOMERS_QUERY_KEY }),
  };
}
